import React, { useState, useRef } from "react";
import {
  Box, Button, Typography, CircularProgress, LinearProgress,
  Snackbar, Alert, IconButton, Card, CardContent
} from "@mui/material";
import { Upload as UploadIcon, Close as CloseIcon } from "@mui/icons-material";
import { useSelector } from "react-redux";
import axiosInstance from "../axiosInstance";

export default function ShippingDocumentAgent() {
  const userId = useSelector((s) => s?.account?.user_id || s?.account?.org_member?.id);
  const orgId = useSelector((s) => s?.account?.org?.id || s?.account?.org_data?.data?.id);
  const account = useSelector(state => state?.account);
  const excelFileId = account?.user?.shp_agent_excel_name;
  const shpAgentExcelUrl = account?.user?.shp_agent_excel_url;

  console.log("account state:", account);
  console.log("shp_agent_excel_name:", excelFileId);

  const baseSharepointUrl = shpAgentExcelUrl
    ? shpAgentExcelUrl +
      '?e=ccgKwP' +
      '&action=embedview' +
      '&AllowTyping=True' +
      '&wdDownloadButton=True' +
      '&wdInConfigurator=True' +
      '&wdHideHeaders=True'
    : null;

  const [billFiles, setBillFiles] = useState([]);
  const [salesFiles, setSalesFiles] = useState([]);
  const [shpinsFiles, setShpinsFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [error, setError] = useState("");
  const [iframeKey, setIframeKey] = useState(Date.now());
  const [showExcel, setShowExcel] = useState(false);
  const [jsonUrl, setJsonUrl] = useState(null);
  const [paidAiResponse, setPaidAiResponse] = useState('');
  const [downloadFilename, setDownloadFilename] = useState('');
  const billInputRef = useRef(null);
  const salesInputRef = useRef(null);
  const shpinsInputRef = useRef(null);

  const handleFileChange = (e, setFunc) => {
    const files = Array.from(e.target.files || []);
    const allowed = files.filter(f =>
      ["pdf", "xls", "xlsx", "csv"].includes(f.name.split(".").pop().toLowerCase())
    );
    setFunc(allowed.slice(0, 1)); // limit to 1 file
    e.target.value = null;
  };

  const clearFile = (setter) => setter([]);

  const handleSubmit = async () => {
    setLoading(true);
    setStatusMessage("📦 Uploading documents…");
    setShowExcel(false);
    setUploadProgress(0);
    setJsonUrl(null);
    setPaidAiResponse('');

    const steps = [
      "🔍 Extracting document contents…",
      "📊 Consolidating into summary…",
      "✅ Complete. Showing results…"
    ];
    let step = 0;
    const interval = setInterval(() => {
      if (step < steps.length) setStatusMessage(steps[step++]);
    }, 2000);

    const formData = new FormData();
    billFiles.forEach(f => formData.append("bill_files", f));
    salesFiles.forEach(f => formData.append("sales_files", f));
    shpinsFiles.forEach(f => formData.append("shpins_files", f));
    formData.append("userId", userId);
    formData.append("orgId", orgId);
    formData.append("fileId", excelFileId);

    try {
      //const res = await axiosInstance.post("http://localhost:8000/document_agent/validate_shipping_documents", formData, {
      const res = await axiosInstance.post("https://api.projectivetech.com/user/validate_shipping_documents", formData, {
        onUploadProgress: e =>
          setUploadProgress(Math.round((e.loaded * 100) / e.total))
      });
      const filename = res.data.download_filename || "AI_Analysis_Report.pdf";
      setDownloadFilename(filename);  // ✅ Store filename in state for use in download
      const { aiAnswer, json_url } = res.data;
      setPaidAiResponse(aiAnswer || '');
      
      setShowExcel(true);
      setDownloadFilename(res.data.download_filename);
      setIframeKey(Date.now());
      setStatusMessage("✅ Completed!");
    } catch (err) {
      console.error(err);
      setError("Upload failed");
      setSnackbarOpen(true);
    } finally {
      clearInterval(interval);
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(
        `https://api.projectivetech.com/user/download_analysis_pdf?filename=${encodeURIComponent(downloadFilename)}`,
        //`http://localhost:8000/document_agent/download_analysis_pdf?filename=${encodeURIComponent(downloadFilename)}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!res.ok) throw new Error("File not found or download failed.");

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', downloadFilename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("Download failed:", err);
      alert("Download failed. Please try again.");
    }
  };

  return (
    <Box display="flex" height="85vh">
      {/* Left Excel Preview */}
      <Box flex={3} display="flex" justifyContent="center" alignItems="center" borderRight="1px solid #ccc" bgcolor="#f8f9fa">
        {loading ? (
          <Box textAlign="center">
            <CircularProgress size={60} />
            <Typography variant="subtitle1" mt={2}>{statusMessage}</Typography>
          </Box>
        ) : showExcel && baseSharepointUrl ? (
          <iframe
            key={iframeKey}
            title="Embedded Excel"
            width="100%"
            height="100%"
            frameBorder="0"
            scrolling="no"
            src={`${baseSharepointUrl}&timestamp=${iframeKey}`}
            style={{ display: 'block' }}
          />
        ) : (
          <Typography color="textSecondary">
            Upload documents to generate Shipping Summary.
          </Typography>
        )}
      </Box>

      {/* Right Upload Panel */}
      <Box flex={1} p={3} sx={{ overflowY: 'auto', bgcolor: '#fafafa' }}>
        <Box display="flex" flexDirection="column" gap={2}>
          {[{
            label: "Bill of Lading",
            ref: billInputRef,
            files: billFiles,
            setFiles: setBillFiles
          }, {
            label: "Sales Order",
            ref: salesInputRef,
            files: salesFiles,
            setFiles: setSalesFiles
          }, {
            label: "Shipping Instruction",
            ref: shpinsInputRef,
            files: shpinsFiles,
            setFiles: setShpinsFiles
          }].map((section, i) => (
            <Box key={i}>
              {/* Label */}
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                {section.label}
              </Typography>

              {/* File name & remove button */}
              {section.files.length > 0 && (
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <Typography variant="body2" color="text.secondary">
                    {section.files[0].name}
                  </Typography>
                  <IconButton size="small" onClick={() => clearFile(section.setFiles)}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}

              {/* Upload button */}
              <Button
                variant="outlined"
                fullWidth
                size="small"
                startIcon={<UploadIcon />}
                onClick={() => section.ref.current?.click()}
              >
                Upload {section.label}
              </Button>

              <input
                hidden
                ref={section.ref}
                type="file"
                onChange={(e) => handleFileChange(e, section.setFiles)}
              />
            </Box>
          ))}
        </Box>

        {/* Submit Button */}
        <Box textAlign="center" mt={3}>
          <Button variant="contained" onClick={handleSubmit}>
            Submit & Validate
          </Button>
        </Box>

        {/* Upload Progress */}
        {uploadProgress > 0 && uploadProgress < 100 && (
          <Box mt={2}>
            <LinearProgress variant="determinate" value={uploadProgress} />
            <Typography variant="caption" display="block" textAlign="center">
              Uploading: {uploadProgress}%
            </Typography>
          </Box>
        )}

        {/* AI Output */}
        {(paidAiResponse['Red Flags'] || paidAiResponse['Analysis']) && (
          <Box mt={3} display="flex" flexDirection="column" gap={2}>
            <Card sx={{ backgroundColor: '#fff3f3' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>🛑 Observations</Typography>
                {(paidAiResponse['Red Flags'] || []).map((item, i) => (
                  <Typography key={i} variant="body2" sx={{ mb: 1 }}>
                    • {item}
                  </Typography>
                ))}
              </CardContent>
            </Card>
            <Card sx={{ backgroundColor: '#f5faff' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>📋 Analysis</Typography>
                {(paidAiResponse['Analysis'] || []).map((item, i) => (
                  <Typography key={i} variant="body2" sx={{ mb: 1 }}>
                    • {item}
                  </Typography>
                ))}
              </CardContent>
            </Card>
            <Button
              variant="outlined"
              onClick={handleDownload}

            >
              Download Shipment Document Report
            </Button>
          </Box>
        )}
      </Box>
      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity="error" onClose={() => setSnackbarOpen(false)}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
}
